import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { ArrowRight, BookOpen, LineChart as LineChartIcon, TrendingUp, Sparkles, Check, Rocket, Crown } from "lucide-react";
import { Link } from "react-router-dom";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { ToggleGroup, ToggleGroupItem } from "../components/ui/toggle-group";
import AssetCard from "../components/AssetCard";
import AutoScrollRow, { RowArrows } from "../components/AutoScrollRow";
import AssetChartDialog from "../components/AssetChartDialog";
import LatestArticlesCarousel from "../components/LatestArticlesCarousel";
import NewsletterForm from "../components/NewsletterForm";
import ReviewsSection from "../components/ReviewsSection";
import HeroChartBackground from "../components/HeroChartBackground";
import { tierStyle } from "../lib/tiers";
import useOfferCarousel from "../lib/useOfferCarousel";
import Seo, { SITE_URL, SITE_NAME } from "../components/Seo";

function useLive(key, endpoint, refetchMs) {
  return useQuery({
    queryKey: [key],
    queryFn: async () => (await api.get(endpoint)).data,
    refetchInterval: refetchMs,
  });
}

// Les 3 points forts : cartes sous le titre (ordinateur uniquement)
const HERO_POINTS = [
  { icon: TrendingUp, label: "Analyses & portefeuilles", value: "Décryptages et suivi en direct", color: "text-emerald-400" },
  { icon: LineChartIcon, label: "Marchés en direct", value: "Crypto, actions, obligations, matières premières", color: "text-violet-400" },
  { icon: BookOpen, label: "Cours & simulateurs", value: "Théorie, puis pratique sans risque", color: "text-orange-400" },
];

function MarketsCarousel() {
  const [timeframe, setTimeframe] = useState("24h");
  const [selected, setSelected] = useState(null);
  const { data, isLoading } = useLive("home-cards", "/market/home-cards", 60_000);
  const rowRef = useRef(null);
  // Masque les actifs sans donnée (ex. Hyperliquid « Indisponible ») pour éviter une carte vide
  const changeField = timeframe === "7d" ? "change_7d_pct" : "change_24h_pct";
  const cards = (data || []).filter((c) => c && c.price != null && c[changeField] != null);

  return (
    <section
      className="mx-auto max-w-7xl px-6 pt-8 pb-10 sm:py-14"
      data-testid="markets-grid-section"
    >
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-emerald-400 mb-2">Marchés en direct</div>
          <h2 className="font-display font-black text-3xl sm:text-4xl">Les actifs à suivre</h2>
          <p className="mt-2 text-sm text-zinc-500 max-w-md">
            Crypto, indices, actions phares, matières premières et taux, mis à jour en continu.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ToggleGroup
            type="single"
            value={timeframe}
            onValueChange={(v) => v && setTimeframe(v)}
            className="border border-white/10 rounded-lg p-1 bg-white/[0.02] mr-2"
            data-testid="timeframe-toggle"
          >
            <ToggleGroupItem value="24h" className="px-4 py-1.5 text-xs uppercase tracking-[0.15em] data-[state=on]:bg-emerald-500 data-[state=on]:text-black" data-testid="toggle-24h">24 h</ToggleGroupItem>
            <ToggleGroupItem value="7d" className="px-4 py-1.5 text-xs uppercase tracking-[0.15em] data-[state=on]:bg-emerald-500 data-[state=on]:text-black" data-testid="toggle-7d">7 j</ToggleGroupItem>
          </ToggleGroup>
          <RowArrows onPrev={() => rowRef.current?.prev()} onNext={() => rowRef.current?.next()} testid="asset-cards" />
        </div>
      </div>

      {isLoading ? (
        <div className="flex gap-4 overflow-x-hidden">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-52 w-[280px] shrink-0 rounded-xl border border-white/5 bg-white/[0.02] animate-pulse" />
          ))}
        </div>
      ) : (
        // Défile tout seul ; souris dessus = pause, puis molette, glisser, doigt ou flèches
        <AutoScrollRow
          ref={rowRef}
          items={cards}
          getKey={(card) => card.key}
          renderItem={(card) => <AssetCard card={card} timeframe={timeframe} onOpen={setSelected} />}
          speed={32}
          testid="asset-cards-grid"
        />
      )}

      <AssetChartDialog card={selected} open={!!selected} onOpenChange={(o) => !o && setSelected(null)} />
    </section>
  );
}

function CoursesBanner() {
  return (
    <section className="mx-auto max-w-7xl px-6 py-10">
      <div className="relative overflow-hidden rounded-2xl border border-orange-500/25 bg-gradient-to-br from-orange-500/[0.10] via-transparent to-transparent p-8 md:p-10">
        <div className="absolute inset-0 grid-noise opacity-30" />
        <div className="relative flex flex-col md:flex-row md:items-center gap-6 md:justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-orange-400 mb-2">Cours & Simulateurs</div>
            <h3 className="font-display font-black text-2xl md:text-3xl">Apprendre puis simuler</h3>
            <p className="mt-2 text-zinc-400 max-w-lg text-sm">
              Des cours pour comprendre les marchés, puis des simulateurs pour tester vos idées sur des données réelles.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button asChild variant="outline" className="border-white/10 bg-transparent hover:bg-white/5" data-testid="courses-cta-cours"><Link to="/cours">Voir les cours</Link></Button>
            <Button asChild className="bg-emerald-500 text-black hover:bg-emerald-300" data-testid="courses-cta-simu"><Link to="/simulateurs">Essayer les outils <ArrowRight className="ml-2 w-4 h-4" /></Link></Button>
          </div>
        </div>
      </div>
    </section>
  );
}

const TIER_ICONS = { essentiel: Rocket, pro: Crown, max: Sparkles };

function GoBeyondSection() {
  const { data: cfg } = useQuery({ queryKey: ["config"], queryFn: async () => (await api.get("/config")).data });
  const tiers = ["essentiel", "pro", "max"];
  const shown = tiers.filter((t) => cfg?.tiers?.[t]);
  const carousel = useOfferCarousel(shown.length, Math.max(0, shown.indexOf("pro")));
  return (
    <section className="border-y border-white/5" data-testid="go-beyond-section">
      <div className="mx-auto max-w-7xl px-6 py-12 sm:py-16">
        <div className="text-center max-w-2xl mx-auto mb-6 sm:mb-10">
          <div className="text-xs uppercase tracking-[0.25em] text-violet-400 mb-3">Abonnements</div>
          <h2 className="font-display font-black text-3xl sm:text-4xl">Aller plus loin avec nous</h2>
          <p className="mt-3 text-zinc-400">
            Analyses approfondies, portefeuilles suivis en direct, canal privé. Chaque offre inclut la précédente.
          </p>
        </div>

        <div
          ref={carousel.ref}
          className="flex md:grid md:grid-cols-3 gap-4 md:gap-5 overflow-x-auto md:overflow-visible snap-x snap-mandatory scrollbar-none -mx-6 px-6 md:mx-0 md:px-0 pt-3 md:pt-0 pb-1 mb-3 md:mb-10"
          data-testid="go-beyond-cards"
        >
          {tiers.map((t) => {
            const meta = cfg?.tiers?.[t];
            if (!meta) return null;
            const Icon = TIER_ICONS[t];
            const featured = t === "pro";
            const s = tierStyle(t); // Essentiel orange · Pro vert · Max violet
            return (
              <Card
                key={t}
                className={`p-6 relative flex flex-col shrink-0 w-[82%] md:w-auto snap-center ${s.soft}`}
                data-testid={`go-beyond-${t}`}
              >
                {featured && <span className="absolute -top-3 left-6 text-[10px] uppercase tracking-[0.25em] bg-emerald-500 text-black px-2 py-0.5 rounded">Recommandé</span>}
                <div className={`w-10 h-10 rounded-md grid place-items-center border ${s.badge}`}>
                  <Icon className={`w-5 h-5 ${s.text}`} />
                </div>
                <div className="mt-4">
                  <h3 className={`font-display font-black text-2xl ${s.title}`}>{meta.name}</h3>
                  <div className="mt-1 flex items-baseline gap-1">
                    <span className="font-mono-num text-3xl font-bold">{meta.price_eur.toString().replace(".", ",")} €</span>
                    <span className="text-zinc-500 text-sm">/mois</span>
                  </div>
                </div>
                <ul className="mt-5 space-y-2 flex-1">
                  {meta.features.slice(0, 4).map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-zinc-300">
                      <Check className={`w-4 h-4 mt-0.5 shrink-0 ${s.text}`} /> {f}
                    </li>
                  ))}
                </ul>
                <Button asChild className={`mt-5 ${featured ? "bg-emerald-500 text-black hover:bg-emerald-300" : "bg-white/10 hover:bg-white/20"}`} data-testid={`go-beyond-cta-${t}`}>
                  <Link to="/upgrade">Choisir {meta.name} <ArrowRight className="ml-2 w-4 h-4" /></Link>
                </Button>
              </Card>
            );
          })}
        </div>

        {/* Téléphone : points sous les cartes (carte affichée) */}
        <div className="md:hidden flex justify-center gap-2 mb-8" aria-hidden="true">
          {shown.map((t, i) => (
            <button
              key={t}
              type="button"
              tabIndex={-1}
              onClick={() => carousel.goTo(i)}
              className={`h-1.5 rounded-full transition-all ${carousel.active === i ? "w-5 bg-white/70" : "w-1.5 bg-white/20"}`}
            />
          ))}
        </div>

        <div className="border-t border-white/5 pt-8 grid gap-6 md:grid-cols-[1fr_auto] md:items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-1">Newsletter gratuite</div>
            <h3 className="font-display font-bold text-xl">Le résumé du mois</h3>
            <p className="text-zinc-500 text-sm mt-1">Désinscription en un clic.</p>
          </div>
          <NewsletterForm />
        </div>
      </div>
    </section>
  );
}

export default function HomePage() {
  const statsRef = useRef(null);
  return (
    <div className="min-h-screen">
      <Seo
        path="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Organization",
          name: SITE_NAME,
          url: SITE_URL,
          logo: `${SITE_URL}/logo-sideb.png`,
          description:
            "Side B : analyses, cours, outils et portefeuilles suivis en direct pour apprendre à investir avec méthode.",
        }}
      />
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 bg-black/70" />
        <HeroChartBackground anchorRef={statsRef} />
        <div className="absolute inset-0 grid-noise opacity-40" />
        <div className="relative mx-auto max-w-7xl px-6 pt-9 pb-10 sm:pt-16 sm:pb-10 md:pt-20 md:pb-12">
          {/* Téléphone : chaque ligne du titre tient sur une seule ligne (taille adaptée à la largeur de l'écran) */}
          <h1 className="font-display font-black text-[length:clamp(1.4rem,6.6vw,2.1rem)] sm:text-5xl lg:text-6xl leading-[1.12] sm:leading-[1.05] max-w-4xl">
            <span className="block whitespace-nowrap"><span className="hero-highlight" style={{ "--i": 0 }}>Apprendre</span> à investir</span>
            <span className="block whitespace-nowrap"><span className="hero-highlight" style={{ "--i": 1 }}>Comprendre</span> les marchés</span>
            <span className="block whitespace-nowrap"><span className="hero-highlight" style={{ "--i": 2 }}>Décider</span> avec méthode</span>
          </h1>
          {/* Téléphone : le titre seul, sans texte d'introduction */}
          <p className="hidden sm:block mt-6 text-lg text-zinc-400 max-w-2xl leading-relaxed">
            Moins de bruit, plus de fond. Nous analysons l'actualité des marchés pour n'en garder que l'essentiel, et vous aidons à décider en connaissance de cause.
          </p>
          <div className="mt-7 sm:mt-8 flex flex-wrap gap-3">
            <Button asChild size="lg" className="h-9 px-4 sm:h-10 sm:px-8 bg-emerald-500 text-black hover:bg-emerald-300" data-testid="hero-cta-sideb">
              <Link to="/portefeuilles-sideb">Voir nos portefeuilles <ArrowRight className="ml-2 w-4 h-4" /></Link>
            </Button>
          </div>
          {/* Ordinateur : 3 cartes (et repère des volumes du graphique). Téléphone : masquées */}
          <div ref={statsRef} className="hidden sm:grid mt-14 grid-cols-1 sm:grid-cols-3 gap-4">
            {HERO_POINTS.map((s) => (
              <div key={s.label} className="p-4 rounded-lg border border-white/5 bg-white/[0.02]">
                <s.icon className={`w-4 h-4 ${s.color}`} />
                <div className="mt-3 text-xs uppercase tracking-[0.15em] text-zinc-500">{s.label}</div>
                <div className="text-sm font-medium mt-1">{s.value}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <MarketsCarousel />
      <LatestArticlesCarousel />
      <CoursesBanner />
      <GoBeyondSection />
      <ReviewsSection />
    </div>
  );
}
